import React, { useEffect } from 'react';
import { Form, Input, Select, DatePicker, Button, Space } from 'antd';
import dayjs from 'dayjs';
import { CrmTaskDTO, TaskPriority, TaskStatus } from '../types';

const { TextArea } = Input;
const { Option } = Select;

interface Props {
    initialValues?: Partial<CrmTaskDTO>;
    custNo?: string;
    isSubmitting?: boolean;
    onSubmit: (values: CrmTaskDTO) => void;
    onCancel: () => void;
}

const PRIORITY_COLOR: Record<TaskPriority, string> = {
    HIGH: '#ff4d4f',
    MEDIUM: '#faad14',
    LOW: '#52c41a',
};

const TaskForm: React.FC<Props> = ({ initialValues, custNo, isSubmitting, onSubmit, onCancel }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                dueDate: initialValues.dueDate ? dayjs(initialValues.dueDate) : undefined,
            });
        } else {
            form.setFieldsValue({
                custNo: custNo || undefined,
                priority: 'MEDIUM',
                status: 'OPEN',
            });
        }
    }, [initialValues, custNo, form]);

    const handleFinish = (values: any) => {
        onSubmit({
            ...values,
            dueDate: values.dueDate?.format('YYYY-MM-DD'),
        });
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleFinish}>
            <Form.Item name="title" label="Task Title" rules={[{ required: true }]}>
                <Input placeholder="What needs to be done?" />
            </Form.Item>

            <div className="grid grid-cols-2 gap-4">
                <Form.Item name="dueDate" label="Due Date" rules={[{ required: true }]}>
                    <DatePicker className="w-full" />
                </Form.Item>
                <Form.Item name="priority" label="Priority">
                    <Select>
                        {(['HIGH', 'MEDIUM', 'LOW'] as TaskPriority[]).map(p => (
                            <Option key={p} value={p}>
                                <span style={{ color: PRIORITY_COLOR[p] }}>● </span>{p}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <Form.Item name="assignedTo" label="Assign To" rules={[{ required: true }]}>
                    <Input placeholder="Officer ID" />
                </Form.Item>
                <Form.Item name="custNo" label="Customer No. (optional)">
                    <Input placeholder="Link to a customer" defaultValue={custNo} />
                </Form.Item>
            </div>

            {initialValues?.id && (
                <Form.Item name="status" label="Status">
                    <Select>
                        {(['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as TaskStatus[]).map(s => (
                            <Option key={s} value={s}>{s.replace('_', ' ')}</Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            <Form.Item name="description" label="Description">
                <TextArea rows={3} placeholder="Additional details..." />
            </Form.Item>

            <div className="flex justify-end">
                <Space>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={isSubmitting}>
                        {initialValues?.id ? 'Update Task' : 'Create Task'}
                    </Button>
                </Space>
            </div>
        </Form>
    );
};

export default TaskForm;
